import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Mail, Phone, Facebook, Twitter, Youtube, Linkedin, MessageCircle } from "lucide-react";

// Pinterest icon (lucide-react doesn't ship one, so a tiny SVG)
const PinIcon = ({ size = 15, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.07 2.43 7.55 5.92 9.12-.08-.78-.15-1.97.03-2.82.16-.77 1.06-4.91 1.06-4.91s-.27-.54-.27-1.34c0-1.25.73-2.19 1.63-2.19.77 0 1.14.58 1.14 1.27 0 .77-.49 1.93-.74 3.01-.21.91.45 1.65 1.34 1.65 1.61 0 2.85-1.7 2.85-4.16 0-2.17-1.56-3.69-3.79-3.69-2.58 0-4.1 1.94-4.1 3.94 0 .78.3 1.62.67 2.08.07.09.08.16.06.25-.07.27-.21.91-.24 1.04-.04.16-.13.2-.3.12-1.13-.52-1.83-2.17-1.83-3.5 0-2.85 2.07-5.47 5.97-5.47 3.13 0 5.57 2.23 5.57 5.22 0 3.11-1.96 5.62-4.69 5.62-.92 0-1.78-.48-2.07-1.04l-.56 2.15c-.21.79-.76 1.79-1.13 2.4.85.26 1.74.4 2.68.4 5.52 0 10-4.48 10-10S17.52 2 12 2z" fill="currentColor"/>
  </svg>
);

// Hard-coded fallback footer columns used when the merchant hasn't customized them in admin
const DEFAULT_COLUMNS = [
  {
    title: "Shop",
    links: [
      { label: "All jewelry", url: "/shop" },
      { label: "Necklaces", url: "/shop/necklaces" },
      { label: "Earrings", url: "/shop/earrings" },
      { label: "Rings", url: "/shop/rings" },
      { label: "Bracelets", url: "/shop/bracelets" },
    ],
  },
  {
    title: "Care",
    links: [
      { label: "Journal", url: "/blog" },
      { label: "My account", url: "/account" },
      { label: "Wishlist", url: "/wishlist" },
      { label: "Contact", url: "/contact" },
      { label: "Track order", url: "/track" },
      { label: "About", url: "/about" },
      { label: "Returns", url: "/returns" },
      { label: "Privacy", url: "/privacy" },
      { label: "Terms", url: "/terms" },
    ],
  },
];

const SOCIALS = [
  { key: "instagram_url", Icon: Instagram, label: "Instagram" },
  { key: "facebook_url", Icon: Facebook, label: "Facebook" },
  { key: "twitter_url", Icon: Twitter, label: "Twitter" },
  { key: "youtube_url", Icon: Youtube, label: "YouTube" },
  { key: "pinterest_url", Icon: PinIcon, label: "Pinterest" },
  { key: "linkedin_url", Icon: Linkedin, label: "LinkedIn" },
  { key: "whatsapp_url", Icon: MessageCircle, label: "WhatsApp" },
];

// Helper to detect external URLs (open in new tab) vs internal paths (use Link)
const isExternal = (url = "") => /^https?:\/\//i.test(url);

const Footer = () => {
  const { branding } = useBranding();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("You're in. Watch your inbox ✨");
      setEmail("");
    } catch {
      toast.error("Please check your email and try again");
    }
  };

  const columns = (branding.footer_columns && branding.footer_columns.length > 0)
    ? branding.footer_columns
    : DEFAULT_COLUMNS;

  const newsletterHeading = branding.footer_newsletter_heading || "Stay in the glow.";
  const newsletterBody = branding.footer_newsletter_body
    || "Subscribe for first access to drops, styling notes and quiet little gifts — crafted just for you.";
  const tagline = branding.footer_tagline || "Handcrafted in India · Shipping worldwide";
  const copyrightLine = branding.footer_copyright
    || `© ${new Date().getFullYear()} ${branding.brand_name || "rieave"} · ${branding.tagline || "Adorn Your Story"}`;

  // Split heading into prefix + last word (italic gold accent on last word for the editorial feel)
  const words = newsletterHeading.trim().split(" ");
  const lastWord = words.pop();
  const headingPrefix = words.join(" ");

  return (
    <footer className="relative bg-charcoal text-cream mt-24" data-testid="site-footer">
      <div className="rieave-container py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="eyebrow text-cream/65 mb-4">Join our world</p>
            <h3 className="font-hero text-4xl md:text-5xl leading-[1.05] mb-5">
              {headingPrefix} <span className="serif-italic text-gold">{lastWord}</span>
            </h3>
            <p className="text-cream/75 max-w-md mb-6">{newsletterBody}</p>
            <form onSubmit={subscribe} className="flex w-full max-w-md" data-testid="newsletter-form">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent border-b border-cream/30 focus:border-gold outline-none py-2.5 font-body placeholder:text-cream/40"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                className="ml-3 font-label text-[12px] uppercase tracking-[0.24em] text-gold hover:text-cream transition"
                data-testid="newsletter-submit"
              >
                Subscribe →
              </button>
            </form>
          </div>

          {/* Dynamic columns from branding */}
          {columns.map((col, idx) => (
            <div key={idx} className="md:col-span-2" data-testid={`footer-col-${idx}`}>
              <p className="eyebrow text-cream/65 mb-4">{col.title}</p>
              <ul className="space-y-2 text-cream/85 font-body text-sm">
                {(col.links || []).map((l, i) => (
                  <li key={i}>
                    {isExternal(l.url) ? (
                      <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`footer-link-${idx}-${i}`}>{l.label}</a>
                    ) : (
                      <Link to={l.url || "/"} data-testid={`footer-link-${idx}-${i}`}>{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-3">
            <p className="eyebrow text-cream/65 mb-4">Contact</p>
            <ul className="space-y-3 text-cream/85 font-body text-sm">
              {branding.support_email && <li className="flex items-center gap-2"><Mail size={15} className="text-gold" /> <a href={`mailto:${branding.support_email}`}>{branding.support_email}</a></li>}
              {branding.support_phone && <li className="flex items-center gap-2"><Phone size={15} className="text-gold" /> <a href={`tel:${branding.support_phone}`}>{branding.support_phone}</a></li>}
            </ul>

            {/* Social row */}
            <div className="flex flex-wrap gap-3 mt-5" data-testid="footer-socials">
              {SOCIALS.filter(s => branding[s.key]).map(({ key, Icon, label }) => (
                <a
                  key={key}
                  href={branding[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-cream/15 hover:border-gold hover:text-gold flex items-center justify-center transition"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="rieave-container py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-label text-[11px] uppercase tracking-[0.24em] text-cream/65">
            {copyrightLine}
          </p>
          <p className="font-label text-[11px] uppercase tracking-[0.24em] text-cream/65">
            {tagline}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
